import { proximoNumeroDeVisita, type Classe } from '@equiview/domain';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  BarraAcoes,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  CampoTexto,
  classeTd,
  classeTh,
  Etiqueta,
  EstadoVazio,
  OpcoesRadio,
  Painel,
  ResumoErros,
  Tabela,
  useAvisoSaida,
} from '../components/ui/index.js';
import {
  casaBusca,
  classeNaData,
  faixaNaData,
  hojeIso,
  idadeDoAnimal,
  ROTULO_CLASSE,
  ROTULO_CLASSE_PLURAL,
  ROTULO_FAIXA,
} from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Haras, Visita } from '../dados/fonte.js';

const CLASSES: Classe[] = ['potro', 'garanhao', 'egua', 'castrado'];

/** Pre-visita: quais animais serao avaliados, e em que nivel. */
export function NovaVisita() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(
    async (f) => {
      const [haras, animais, visitas] = await Promise.all([
        f.haras(),
        f.listarAnimais(),
        f.cadastro ? f.cadastro.visitas() : Promise.resolve([]),
      ]);
      return { haras, animais, visitas };
    },
    [],
  );

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {({ haras, animais, visitas }) =>
        !haras ? (
          <EstadoVazio titulo="Cadastre um haras primeiro" acao={<BotaoLink to="/haras/novo">Cadastrar haras</BotaoLink>} />
        ) : (
          <Planejamento haras={haras} animais={animais} visitas={visitas} />
        )
      }
    </Aguardar>
  );
}

function Planejamento({ haras, animais, visitas }: { haras: Haras; animais: Animal[]; visitas: Visita[] }) {
  const fonte = useFonte();
  const navegar = useNavigate();
  const doHaras = animais
    .filter((a) => a.owner_farm_id === haras.id)
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  const [data, setData] = useState(hojeIso());
  const [numero, setNumero] = useState(String(proximoNumeroDeVisita(visitas)));
  // Todo animal entra marcado e no Nivel 2; a equipe tira quem nao vai e troca para censo.
  const [incluidos, setIncluidos] = useState<Set<string>>(() => new Set(doHaras.map((a) => a.id)));
  const [nivel, setNivel] = useState<Record<string, 1 | 2>>({});
  const [proposito, setProposito] = useState<'rotina' | 'estudo_repetibilidade'>('rotina');
  const [busca, setBusca] = useState('');
  const [tentou, setTentou] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [mexeu, setMexeu] = useState(false);

  useAvisoSaida(mexeu && !salvo && !salvando);

  const linhas = doHaras.map((a) => {
    const classe = classeNaData(a, animais, data || hojeIso());
    return { animal: a, classe, faixa: classe === 'potro' ? faixaNaData(a, data || hojeIso()) : null };
  });
  const visiveis = linhas.filter((l) => casaBusca(l.animal, busca));
  const nivelDe = (id: string) => nivel[id] ?? 2;
  const escolhidos = doHaras.filter((a) => incluidos.has(a.id));
  const completos = escolhidos.filter((a) => nivelDe(a.id) === 2).length;

  const n = Number(numero);
  const erros = [
    ...(!data ? [{ campo: 'data', mensagem: 'Informe a data da visita.' }] : []),
    ...(!Number.isInteger(n) || n < 1 ? [{ campo: 'numero', mensagem: 'O número da visita precisa ser um inteiro positivo.' }] : []),
    ...(visitas.some((v) => v.visit_number === n) ? [{ campo: 'numero', mensagem: `Já existe a visita nº ${n} neste haras.` }] : []),
    ...(escolhidos.length === 0 ? [{ campo: 'animais', mensagem: 'Inclua pelo menos um animal.' }] : []),
  ];
  const erro = (campo: string) => (tentou ? erros.find((e) => e.campo === campo)?.mensagem : undefined);

  const alternar = (id: string) => {
    setMexeu(true);
    setIncluidos((s) => {
      const novo = new Set(s);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };
  const marcarClasse = (c: Classe | 'todos' | 'nenhum') => {
    setMexeu(true);
    setIncluidos(
      new Set(c === 'nenhum' ? [] : linhas.filter((l) => c === 'todos' || l.classe === c).map((l) => l.animal.id)),
    );
  };

  const salvar = async () => {
    setTentou(true);
    if (erros.length) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSalvando(true);
    const id = await fonte.cadastro!.planejarVisita({
      visit_date: data,
      visit_number: n,
      purpose: proposito,
      itens: escolhidos.map((a) => ({ animal_id: a.id, protocol_level: nivelDe(a.id) })),
    });
    setSalvo(true);
    navegar(`/visitas/${id}`, { replace: true });
  };

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[{ rotulo: 'Visitas', para: '/visitas' }, { rotulo: 'Nova visita' }]}
        sobretitulo={haras.name}
        titulo="Planejar visita"
        meta={<span>Escolha os animais e o nível de avaliação. Tudo fica no tablet para o dia da visita, mesmo sem internet.</span>}
      />

      {tentou && <ResumoErros erros={erros} />}

      <Painel titulo="Visita">
        <div className="grid gap-4 sm:grid-cols-[12rem_8rem]">
          <CampoTexto rotulo="Data" obrigatorio type="date" value={data} onChange={(e) => {
              setMexeu(true);
              setData(e.target.value);
            }} erro={erro('data')} />
          <CampoTexto
            rotulo="Número"
            obrigatorio
            inputMode="numeric"
            value={numero}
            onChange={(e) => {
              setMexeu(true);
              setNumero(e.target.value.replace(/\D/g, ''));
            }}
            erro={erro('numero')}
            ajuda="Sugerido: o próximo."
          />
        </div>
        <div className="mt-4">
          <OpcoesRadio
            rotulo="Tipo de visita"
            valor={proposito}
            onMudar={(v) => {
              setMexeu(true);
              setProposito(v);
            }}
            opcoes={[
              { valor: 'rotina', rotulo: 'Rotina' },
              { valor: 'estudo_repetibilidade', rotulo: 'Estudo de repetibilidade (Fase 0)' },
            ]}
            ajuda={
              proposito === 'estudo_repetibilidade'
                ? 'Cada avaliador mede cada animal duas vezes, às cegas. É o teste que decide quais medidas entram no protocolo.'
                : 'Coleta normal. 10% das fichas são sorteadas para a dupla medição.'
            }
          />
        </div>
      </Painel>

      <Painel
        titulo={`Animais do plantel (${doHaras.length})`}
        corpo="p-0"
        acoes={
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar"
            aria-label="Buscar animal"
            className="w-44 rounded-(--radius-controle) border border-borda-forte bg-superficie px-2 text-sm outline-none focus:border-destaque"
          />
        }
      >
        {doHaras.length === 0 ? (
          <div className="p-4">
            <EstadoVazio
              titulo="O plantel está vazio"
              acao={
                <BotaoLink variante="principal" to="/animais/novo">
                  Cadastrar animal
                </BotaoLink>
              }
            />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1 border-b border-borda px-4 py-2 text-[13px]">
              <span className="mr-1 text-apagado">Marcar:</span>
              {(['todos', ...CLASSES, 'nenhum'] as const).map((c) => (
                <Botao key={c} tamanho="sm" variante="discreto" onClick={() => marcarClasse(c)}>
                  {c === 'todos' ? 'Todos' : c === 'nenhum' ? 'Nenhum' : ROTULO_CLASSE_PLURAL[c]}
                </Botao>
              ))}
            </div>
            <Tabela rotulo="Animais da visita">
              <thead>
                <tr>
                  <th scope="col" className={`${classeTh} w-12`}>
                    <span className="sr-only">Incluir</span>
                  </th>
                  <th scope="col" className={classeTh}>
                    Animal
                  </th>
                  <th scope="col" className={`${classeTh} hidden sm:table-cell`}>
                    Classe na visita
                  </th>
                  <th scope="col" className={`${classeTh} hidden text-right md:table-cell`}>
                    Idade na visita
                  </th>
                  <th scope="col" className={classeTh}>
                    Nível
                  </th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map(({ animal, classe, faixa }) => {
                  const dentro = incluidos.has(animal.id);
                  return (
                    <tr key={animal.id} className={dentro ? '' : 'opacity-55'}>
                      <td className={classeTd}>
                        <input
                          type="checkbox"
                          checked={dentro}
                          onChange={() => alternar(animal.id)}
                          aria-label={`Incluir ${animal.name}`}
                          className="size-5 min-h-0 accent-(--color-marca)"
                        />
                      </td>
                      <td className={classeTd}>
                        <span className="font-serif font-semibold text-marca">{animal.name}</span>
                        <span className="block font-mono text-xs text-apagado">{animal.abccmm_registry ?? 'Sem registro'}</span>
                      </td>
                      <td className={`${classeTd} hidden sm:table-cell`}>
                        <Etiqueta>{ROTULO_CLASSE[classe]}</Etiqueta>
                        {faixa && <span className="ml-1.5 text-xs text-apagado">{ROTULO_FAIXA[faixa]}</span>}
                      </td>
                      <td className={`${classeTd} numero hidden text-right md:table-cell`}>
                        {idadeDoAnimal(animal, data || hojeIso())}
                      </td>
                      <td className={classeTd}>
                        <OpcoesRadio
                          rotulo={`Nível de ${animal.name}`}
                          tamanho="sm"
                          valor={dentro ? String(nivelDe(animal.id)) : ''}
                          onMudar={(v) => {
                            setMexeu(true);
                            setNivel((m) => ({ ...m, [animal.id]: v === '1' ? 1 : 2 }));
                            if (!dentro) alternar(animal.id);
                          }}
                          opcoes={[
                            { valor: '2', rotulo: 'Completo' },
                            { valor: '1', rotulo: 'Censo' },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          </>
        )}
      </Painel>

      <BarraAcoes
        aviso={
          <span className="numero text-sm text-texto">
            <strong className="font-semibold">{escolhidos.length}</strong> animais · {completos} completos ·{' '}
            {escolhidos.length - completos} censo
          </span>
        }
      >
        <BotaoLink to="/visitas">Cancelar</BotaoLink>
        <Botao variante="principal" onClick={() => void salvar()} carregando={salvando}>
          Salvar visita
        </Botao>
      </BarraAcoes>
    </div>
  );
}
